/**
 * Display-side projection: turn typed AI suggestion ROWS (the source of
 * truth, synced into Dexie) into the `ai_suggestions[]` block shape the recap
 * read paths already understand (`recapService` helpers, the cells / badges /
 * banners, `aiAcceptVisibility`, and the `aiResolveLocally` merge math). This
 * is the read-time inverse of the resolver's row→block adapter, so the ~15
 * display readers keep working unchanged while the source of truth moves to
 * typed rows.
 *
 * Pending only: a resolved row (`acceptedAt` / `declinedAt` set) or a
 * tombstoned one (`deletedAt`) is not projected — the recap should only
 * surface live proposals. AI-created flows (`flow.status = 5`, no row) get a
 * synthetic `type:add` block so the "pending add" paint still fires.
 *
 * Prefer-typed-rows contract: when a target has a typed row we OVERWRITE its
 * embedded `ai_suggestions` (so a stale JSON block from the legacy path can't
 * double-render). A target with NO typed row keeps whatever metadata it
 * already carries (legacy fallback during the transition).
 */

import type { PreOrderSuggestion } from "@stockline/shared/types/ai/pre-order-suggestion";
import type { PreOrderFlowSuggestion } from "@stockline/shared/types/ai/pre-order-flow-suggestion";
import type { PreOrder as CamelPreOrder } from "@stockline/shared/types/orders/pre-order";
import type { PreOrderFlow as CamelPreOrderFlow } from "@stockline/shared/types/orders/pre-order-flow";
import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import {
  type AdaptedSuggestion,
  buildFlowSuggestionMap,
  buildHeaderSuggestionMap,
  syntheticAddBlock,
} from "./aiResolveLocally/adapter";
import {
  FLOW_METADATA_BLOB_FIELD,
  isPlainObject,
  jsonEqual,
} from "./aiResolveLocally/helpers";

const AI_SUGGESTIONS_KEY = "ai_suggestions";
const AI_SUGGESTION_LEGACY_KEY = "ai_suggestion";
const FLOW_STATUS_AI_PROPOSAL = 5;

export interface SuggestionMaps {
  /** committed pre-order id → adapted header suggestion. */
  header: Map<string, AdaptedSuggestion>;
  /** committed flow id → adapted flow suggestion. */
  flow: Map<string, AdaptedSuggestion>;
}

/** Build both lookup maps from the raw row arrays in one pass. */
export function buildSuggestionMaps(
  headerRows: PreOrderSuggestion[],
  flowRows: PreOrderFlowSuggestion[],
): SuggestionMaps {
  return {
    header: buildHeaderSuggestionMap(headerRows),
    flow: buildFlowSuggestionMap(flowRows),
  };
}

/** Merge a single block into a metadata object under the canonical key,
 * dropping any legacy singleton so it can't shadow the projected array. */
function withBlock(
  metadata: Record<string, unknown> | null | undefined,
  block: AdaptedSuggestion["block"],
): Record<string, unknown> {
  const base =
    metadata && typeof metadata === "object" && !Array.isArray(metadata)
      ? { ...metadata }
      : {};
  delete base[AI_SUGGESTION_LEGACY_KEY];
  base[AI_SUGGESTIONS_KEY] = [block];
  return base;
}

/**
 * Overlay a pending MODIFY block's proposed `flowMetadata.delineation`
 * (STK-1571) and `.source_quote` (STK-1811) onto the metadata's matching
 * top-level keys, each wholesale-replaced independently. This mirrors the
 * accept-time `assembleResolvedMetadata` (`Object.assign(merged, aiMetaBlob)`),
 * so the pre-accept hover preview — both the attachment highlight and the
 * mail-body quote highlight — equals the post-accept result.
 *
 * The backend already bakes the FULL merged `flow_metadata` into
 * `metadataPatch` (the block's `flowMetadata.to` — see
 * `pre_order_tools.py`'s `_deep_merge_metadata` write), so no frontend
 * re-merge is needed — and replacing (not merging) each key also clears a
 * stale value when the AI's re-resolution came back empty (e.g. a
 * `source_quote: null` re-propose that matched nothing).
 *
 * No-op (same reference) for non-modify blocks, blocks that didn't touch
 * `flowMetadata`, or a `to` carrying neither key. Scoped to these two keys on
 * purpose — they are the only ones a hover reads live. Notably the `ai` block
 * is NOT overlaid: it carries `ask`, which two surfaces render as a line name
 * (`MobileProposalLines`, `AiPreOrderCard`), and previewing a staged quote
 * must not restate those. Every other staged field renders from the
 * `ai_suggestions[]` block's own `from`/`to` diff, not a merged preview.
 */
function overlayModifyPreviewFields(
  metadata: Record<string, unknown>,
  block: AdaptedSuggestion["block"],
): Record<string, unknown> {
  if (block.type !== "modify") return metadata;
  const to = block.changes?.[FLOW_METADATA_BLOB_FIELD]?.to;
  if (!isPlainObject(to)) return metadata;

  const patch: Record<string, unknown> = {};
  if (
    Array.isArray(to.delineation) &&
    !jsonEqual(metadata.delineation, to.delineation)
  ) {
    patch.delineation = to.delineation;
  }
  if (
    "source_quote" in to &&
    !jsonEqual(metadata.source_quote, to.source_quote)
  ) {
    patch.source_quote = to.source_quote;
  }
  if (Object.keys(patch).length === 0) return metadata;
  return { ...metadata, ...patch };
}

function projectFlow(
  flow: PreOrderFlow,
  flowMap: Map<string, AdaptedSuggestion>,
): PreOrderFlow {
  const adapted = flowMap.get(flow.id);
  if (adapted) {
    return {
      ...flow,
      flow_metadata: overlayModifyPreviewFields(
        withBlock(flow.flow_metadata, adapted.block),
        adapted.block,
      ),
    };
  }
  // AI-created line: status is the pending marker, no sidecar row exists.
  if (flow.status === FLOW_STATUS_AI_PROPOSAL) {
    return {
      ...flow,
      flow_metadata: withBlock(flow.flow_metadata, syntheticAddBlock()),
    };
  }
  return flow;
}

/**
 * Project typed suggestion rows onto a standalone flow list (the Dexie
 * `liveFlows` a section renders). Identity-stable: returns the same array when
 * nothing applies so `React.memo` flow rows don't churn.
 *
 * No `maps.flow.size === 0` fast path: AI-created flows (`status = 5`) carry NO
 * sidecar row, so an empty flow map does NOT mean there's nothing to project —
 * `projectFlow` still synthesizes their `type:add` block from status alone. The
 * per-element identity check below preserves the same-reference guarantee, so
 * the missing fast path costs only a cheap O(n) walk.
 */
export function projectFlows(
  flows: PreOrderFlow[],
  maps: SuggestionMaps,
): PreOrderFlow[] {
  let changed = false;
  const out = flows.map((f) => {
    const next = projectFlow(f, maps.flow);
    if (next !== f) changed = true;
    return next;
  });
  return changed ? out : flows;
}

/**
 * Project typed suggestion rows onto a single pre-order: replace its header
 * and per-flow `ai_suggestions` with the rows when present. Returns the same
 * reference when nothing applies, so memoized consumers don't churn.
 */
export function projectPreOrder(
  preOrder: PreOrder,
  maps: SuggestionMaps,
): PreOrder {
  const header = maps.header.get(preOrder.id);
  const flows = Array.isArray(preOrder.flows) ? preOrder.flows : [];
  const projectedFlows = flows.map((f) => projectFlow(f, maps.flow));
  const flowsChanged = projectedFlows.some((f, i) => f !== flows[i]);

  if (!header && !flowsChanged) return preOrder;

  const out: PreOrder = { ...preOrder };
  if (header) {
    out.pre_order_metadata = withBlock(
      preOrder.pre_order_metadata,
      header.block,
    );
  }
  if (flowsChanged) out.flows = projectedFlows;
  return out;
}

/**
 * Project a whole list. Identity-stable per element.
 *
 * No empty-maps fast path: an AI-created flow (`status = 5`) has no sidecar row,
 * so even with both maps empty `projectPreOrder` must still synthesize its
 * `type:add` block. The per-element identity check keeps the same-reference
 * guarantee for untouched pre-orders, so skipping the fast path is cheap.
 */
export function projectPreOrders(
  preOrders: PreOrder[],
  maps: SuggestionMaps,
): PreOrder[] {
  let changed = false;
  const out = preOrders.map((po) => {
    const next = projectPreOrder(po, maps);
    if (next !== po) changed = true;
    return next;
  });
  return changed ? out : preOrders;
}

// ---------------------------------------------------------------------------
// camelCase projection (offline-first Dexie shape)
//
// The recap reads junction-enriched snake_case `PreOrder`/`PreOrderFlow` (nested
// `flows`, `pre_order_metadata`, `flow_metadata`). The offline-first hooks
// (`usePreOrders` / `usePreOrderFlows`, consumed by the mobile proposal surface
// and `useAiPendingPreOrders`) return the canonical Dexie camelCase rows
// (`preOrderMetadata` / `flowMetadata`, flat flow list keyed by `preOrderId`).
//
// Same adapter, same `SuggestionMaps`, same "prefer typed rows, keep legacy
// embedded blocks as fallback" contract — only the metadata key differs. This
// keeps a SINGLE projection source: `buildSuggestionMaps` (the row→block
// adapter) is shared; only the write target changes.
// ---------------------------------------------------------------------------

/** Project typed suggestion rows onto a Dexie (camelCase) flow. Returns the
 * same reference when nothing applies. */
export function projectCamelFlow(
  flow: CamelPreOrderFlow,
  maps: SuggestionMaps,
): CamelPreOrderFlow {
  const adapted = maps.flow.get(flow.id);
  if (adapted) {
    return {
      ...flow,
      flowMetadata: overlayModifyPreviewFields(
        withBlock(flow.flowMetadata, adapted.block),
        adapted.block,
      ),
    };
  }
  // AI-created line: status is the pending marker, no sidecar row exists.
  if (flow.status === FLOW_STATUS_AI_PROPOSAL) {
    return {
      ...flow,
      flowMetadata: withBlock(flow.flowMetadata, syntheticAddBlock()),
    };
  }
  return flow;
}

/** Project typed suggestion rows onto a Dexie (camelCase) flow list.
 * Identity-stable: same array reference when nothing applies. Walks even with
 * no flow rows when an AI-created line (`status = AI_PROPOSAL`, no row) is
 * present, so its synthetic `type:add` block still gets stamped. */
export function projectCamelFlows(
  flows: CamelPreOrderFlow[],
  maps: SuggestionMaps,
): CamelPreOrderFlow[] {
  const hasAiCreated = flows.some((f) => f.status === FLOW_STATUS_AI_PROPOSAL);
  if (maps.flow.size === 0 && !hasAiCreated) return flows;
  let changed = false;
  const out = flows.map((f) => {
    const next = projectCamelFlow(f, maps);
    if (next !== f) changed = true;
    return next;
  });
  return changed ? out : flows;
}

/** Project typed header suggestion rows onto a Dexie (camelCase) pre-order's
 * `preOrderMetadata`. Flows are projected separately via `projectCamelFlows`
 * (the camelCase pre-order carries no nested flow list). Returns the same
 * reference when no header row applies. */
export function projectCamelPreOrders(
  preOrders: CamelPreOrder[],
  maps: SuggestionMaps,
): CamelPreOrder[] {
  if (maps.header.size === 0) return preOrders;
  let changed = false;
  const out = preOrders.map((po) => {
    const header = maps.header.get(po.id);
    if (!header) return po;
    changed = true;
    return {
      ...po,
      preOrderMetadata: withBlock(po.preOrderMetadata, header.block),
    };
  });
  return changed ? out : preOrders;
}
